/**
 * /how-to-choose-doctor-vietnam
 *
 * Single SEO landing page — How to Choose a Doctor in Vietnam.
 * Educational guide with practical checklists, comparison criteria,
 * red flags to watch for, and questions to ask. Reuses existing site
 * components for branding/palette — no new design work.
 *
 * Copy safety:
 *   • Independent platform, not a hospital/clinic.
 *   • No medical diagnosis, no result guarantees.
 *   • Never claims "the best doctor".
 *   • Educational tone — does not attack any provider.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, ShieldCheck, MessageCircle, ClipboardCheck, AlertTriangle,
  CheckCircle2, GraduationCap, Heart, AlertCircle,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentIntro, UrgentCardGrid, UrgentSteps, UrgentFaq, UrgentEditorial,
  UrgentCtaBand,
} from '../components/UrgentCareSections';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { useCatalog } from '../context/CatalogContext';
import { getInfoLandingImage } from '../lib/imageOverrides';

const SITE = 'https://vietclinics.com';
const SLUG = '/how-to-choose-doctor-vietnam';

const HERO_IMAGE =
  'https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&cs=tinysrgb&w=1400';
const HERO_FALLBACK =
  'https://images.pexels.com/photos/3845625/pexels-photo-3845625.jpeg?auto=compress&cs=tinysrgb&w=1400';

const WHY_CHOICE_MATTERS = [
  { title: 'Outcomes vary by experience',         body: 'Doctor experience with your specific case type often matters more than the headline qualification — especially for surgery, dental restoration, fertility and complex specialties.' },
  { title: 'Communication is part of care',       body: 'A doctor who can explain your diagnosis, options, risks and aftercare in a language you understand is part of safe treatment, not a nice-to-have.' },
  { title: 'Facility standards matter',            body: 'Where treatment happens — licensed clinic, day surgery centre or hospital — affects safety, anaesthesia care and complication response. This is part of the choice.' },
  { title: 'Aftercare changes the outcome',        body: 'Choosing a doctor with a clear written aftercare and revision policy is often the difference between a smooth recovery and a stressful one.' },
];

const COMPARISON_FACTORS = [
  'Doctor licensing and formal qualifications',
  'Years of experience and case volume in your treatment',
  'Where the treatment will be performed (licensed clinic vs hospital)',
  'Clinical standards: anaesthesia, sterilisation, equipment, ICU access',
  'Communication quality and language support',
  'International patient experience and aftercare protocols',
  'Transparent quotes — what is included, what is not',
  'Written follow-up and revision policy',
  'Patient reviews and before-and-after evidence where appropriate',
  'How responsive the doctor or clinic is during your enquiry',
];

const CHECKLIST_BEFORE_DECIDING = [
  'Verify the doctor is licensed for the specific treatment',
  'Confirm formal qualifications and any post-graduate training',
  'Ask how many similar cases the doctor has performed',
  'Confirm where the procedure will take place (and that the facility is licensed)',
  'Understand the proposed treatment plan and timeline in writing',
  'Ask about risks, possible complications and how they are handled',
  'Ask exactly what is included in the quote — and what is not',
  'Ask what the recovery looks like and how long to stay in Vietnam',
  'Confirm the written follow-up and revision policy',
  'Confirm who is responsible if you have concerns after treatment',
  'Check independent patient reviews where available',
  'Confirm communication will be clear in your language',
];

const RED_FLAGS = [
  { title: 'Refuses to share qualifications',  body: 'Any qualified doctor will share their credentials, training and case experience on request. Reluctance is a real signal.' },
  { title: 'Pressure to decide today',         body: 'Healthcare decisions should not feel rushed. A reputable doctor encourages a consultation, time to read the plan, and (where useful) a second opinion.' },
  { title: 'No written treatment plan',         body: 'You should receive the proposed treatment in writing — diagnosis context, procedure, recovery timing, and price breakdown. Verbal-only quotes make comparison and consent harder.' },
  { title: 'Guaranteed results',                body: 'No ethical doctor guarantees a specific outcome in advance. Realistic expectations and documented complication policies are better signals.' },
  { title: 'Per-graft / per-tooth surprises',   body: 'For hair transplants, implants and similar treatments, the total inclusive price should be agreed upfront — not after you arrive.' },
  { title: 'Aftercare not in writing',          body: 'Insist on a written aftercare and revision policy. If it cannot be put in writing, it cannot be relied on.' },
];

const QUESTIONS_TO_ASK = [
  'Are you licensed for this specific treatment?',
  'How many similar cases have you performed?',
  'Where will the treatment take place — clinic or hospital?',
  'What are the risks and possible complications?',
  'What outcomes are realistic for my case?',
  'What is included in the price, and what is not?',
  'How long should I stay in Vietnam for safe treatment and recovery?',
  'What aftercare and follow-up is included?',
  'Who do I contact if I have concerns after treatment?',
  'Can I see before-and-after evidence and patient reviews?',
];

const SECOND_OPINION_REASONS = [
  'You are not sure about the recommended treatment',
  'The plan or quote feels rushed or unclear',
  'You want to compare treatment approaches across doctors',
  'The condition is complex or has long-term implications',
  'You feel uncertain about the risks or aftercare',
  'You want confidence before travelling',
];

const SUPPORT_JOURNEY = [
  { title: 'Tell us about your case',         body: 'A short message about your treatment, city and timing. There is no charge to enquire.' },
  { title: 'We share suitable options',       body: 'You receive doctor and clinic options from our verified partner network with credentials and language notes.' },
  { title: 'We help you compare',              body: 'Use our framework to compare options side-by-side — experience, facility, communication, price and aftercare.' },
  { title: 'Consultations are arranged',       body: 'Online or in-person, with tailored question sets so you can decide with confidence.' },
  { title: 'You choose — without pressure',    body: 'You decide which doctor to proceed with. We coordinate appointments, travel and follow-up once you choose.' },
];

const RELATED_LINKS = [
  { to: '/doctor-recommendation-vietnam',  label: 'Doctor Recommendation in Vietnam',  sub: 'See suitable, licensed doctor options for your case' },
  { to: '/medical-tourism-support-vietnam', label: 'Medical Tourism Support in Vietnam', sub: 'Treatment, travel & patient coordination for international patients' },
  { to: '/patient-support-vietnam',        label: 'Patient Support in Vietnam',        sub: 'Coordination, translator help and aftercare' },
  { to: '/medical-translator-vietnam',     label: 'Medical Translator & Communication Support', sub: 'Appointment translation, question prep & treatment plan explanation' },
  { to: '/how-it-works',                   label: 'How VietClinics Works',             sub: 'The 4-step concierge process explained' },
  { to: '/cosmetic-surgery-in-vietnam',    label: 'Cosmetic Surgery in Vietnam',       sub: 'Choosing a verified plastic surgeon at a JCI-accredited hospital' },
  { to: '/dental-treatments-in-vietnam',   label: 'Dental Treatment in Vietnam',       sub: 'Choosing a dentist for implants, veneers or full-mouth restoration' },
  { to: '/hair-restoration-in-vietnam',    label: 'Hair Transplant in Vietnam',        sub: 'Choosing a hair restoration surgeon — FUE vs DHI' },
  { to: '/eye-care-treatments-in-vietnam', label: 'Eye Treatment in Vietnam',          sub: 'Choosing the right LASIK / cataract / retina specialist' },
  { to: '/urgent-medical-care',            label: 'Urgent Medical Care in Vietnam',    sub: 'For non-emergency urgent care while travelling' },
  { to: '/contact',                        label: 'Contact VietClinics',               sub: 'Talk to a coordinator on WhatsApp or email' },
];

const FAQ = [
  { q: 'How do I find a good doctor in Vietnam?',                       a: 'Start by clarifying your treatment, city and timing — then compare doctor and clinic options against the criteria on this page: licensing, experience, where treatment is performed, communication quality, indicative pricing, and written aftercare. VietClinics shares suitable options from our verified partner network so you do not have to start from scratch.' },
  { q: 'How do I know if a doctor in Vietnam is licensed?',             a: 'Every qualified doctor in Vietnam holds a practising license issued by the Vietnam Ministry of Health. Reputable doctors share license details, formal qualifications and any post-graduate training on request. If a doctor or clinic refuses to share these, treat it as a red flag.' },
  { q: 'What questions should I ask before choosing a doctor?',         a: 'Ask whether the doctor is licensed for the specific treatment, how many similar cases they have performed, where the procedure will take place, what is included in the price, what aftercare is provided, and who is responsible if you have concerns after treatment. The 10-question list on this page is a good starting point.' },
  { q: 'Should I choose a doctor based on price?',                       a: 'Price is one factor — but rarely the most important one. Safety, experience, facility standards, communication and aftercare often have a much bigger impact on outcomes than a small price difference. We help you compare on what actually matters for your case.' },
  { q: 'Can I get a second medical opinion in Vietnam?',                 a: 'Yes — and for complex or expensive treatments we often suggest it. A second opinion can confirm the proposed approach, surface alternative options, or simply give you confidence. VietClinics can coordinate second-opinion consultations with another doctor from our partner network.' },
  { q: 'How do I compare clinics and hospitals in Vietnam?',            a: 'Compare on accreditation (e.g. JCI), the team\'s experience, where surgery would take place, anaesthesia and ICU capability, infection-control protocols, English-language support, transparent pricing and written aftercare/revision policies — not just on the marketing brochure.' },
  { q: 'Are foreign-trained doctors better in Vietnam?',                a: 'Not automatically. Many excellent doctors in Vietnam trained domestically; many trained abroad. What matters is experience with your specific treatment, the facility where you will be treated, and the way the doctor communicates and follows up with you.' },
  { q: 'Can VietClinics help me choose between doctors?',                a: 'Yes. We share suitable doctor options with credentials, experience notes, language information and indicative pricing — and help you compare on the criteria that matter. We never claim "the best doctor in Vietnam" — that is a guidance question, not a fact.' },
  { q: 'How long does it take to choose the right doctor?',              a: 'For straightforward treatments, you can often choose within a few days of consultation. For surgery, complex dental work or fertility, allow longer — most patients benefit from one or two consultations before committing.' },
  { q: 'Is VietClinics a clinic or hospital?',                            a: 'No. VietClinics is an independent healthcare coordination platform. We do not provide medical advice, diagnosis or treatment ourselves. All medical advice and treatment decisions come from licensed healthcare professionals at our partner clinics and hospitals.' },
];

const EDITORIAL = {
  reviewedBy: 'VietClinics Patient Concierge Team',
  role: 'Doctor selection & patient coordination',
  methodology:
    'This guide is based on the same internal checklist VietClinics uses when vetting partner doctors and clinics — covering qualifications, scope of practice, facility standards, language support, transparent pricing and aftercare. The framework is reviewed quarterly and refined from real patient feedback.',
  lastUpdated: '2026-02-15',
};

const HowToChooseDoctorVietnamPage = () => {
  const { contactInfo } = useContactInfo();
  const { overrides } = useCatalog();
  const heroImage = getInfoLandingImage(overrides, 'how-to-choose-doctor', HERO_IMAGE);
  const whatsappHref = getWhatsAppHref(
    contactInfo,
    'Hi VietClinics, I need help choosing the right doctor in Vietnam.'
  );
  const canonical = `${SITE}${SLUG}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${canonical}#article`,
        headline: 'How to Choose a Doctor in Vietnam',
        description:
          'A practical patient guide to choosing a licensed, suitable doctor in Vietnam — comparison criteria, red flags, questions to ask, and when to get a second opinion. By VietClinics.',
        author: { '@type': 'Organization', name: 'VietClinics Patient Concierge Team' },
        publisher: { '@type': 'Organization', name: 'VietClinics' },
        url: canonical,
        image: heroImage,
        datePublished: EDITORIAL.lastUpdated,
        dateModified: EDITORIAL.lastUpdated,
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: 'How to Choose a Doctor in Vietnam',
        description:
          'A clear, independent patient guide to choosing a doctor in Vietnam — comparison criteria, red flags, what to ask, and when to seek a second opinion.',
        inLanguage: 'en',
        dateModified: EDITORIAL.lastUpdated,
        lastReviewed: EDITORIAL.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: EDITORIAL.reviewedBy },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: FAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Patient Support', item: `${SITE}/patient-support-vietnam` },
          { '@type': 'ListItem', position: 3, name: 'How to Choose a Doctor', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>How to Choose a Doctor in Vietnam | Questions to Ask Before Treatment | VietClinics</title>
        <meta
          name="description"
          content="A clear, independent patient guide to choosing a doctor in Vietnam — comparison criteria, red flags, the 10 questions to ask, and when to get a second opinion. By VietClinics."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="How to Choose a Doctor in Vietnam | VietClinics" />
        <meta
          property="og:description"
          content="A practical patient guide to choosing a licensed, suitable doctor in Vietnam — what to check, what to ask, and what to avoid."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white" data-testid="choose-doctor-hero">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)',
          }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                Patient guide · Vietnam
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                How to Choose a Doctor in Vietnam
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                A practical, independent guide for international patients, expats and
                travelers — what to check, what to ask, what to avoid, and how to
                feel confident in your decision.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/doctor-recommendation-vietnam"
                  className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#138F8A] text-white font-bold rounded-full px-6 py-3.5 transition-colors"
                  data-testid="choose-doctor-cta-recommendations"
                >
                  See Doctor Recommendations
                  <ArrowRight className="h-4 w-4" />
                </Link>
                {whatsappHref && (
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-[#1FA7A1] font-bold rounded-full px-6 py-3.5 transition-colors"
                    data-testid="choose-doctor-cta-help"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Ask for Help Choosing
                  </a>
                )}
              </div>
            </div>
            <div
              className="relative rounded-3xl overflow-hidden bg-slate-100"
              style={{ aspectRatio: '4 / 3' }}
            >
              <img
                src={heroImage}
                onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
                alt="Patient choosing a doctor in Vietnam with VietClinics guidance"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ──────────────────────────────────────────────────── */}
      <section className="bg-white border-y border-slate-100" data-testid="choose-doctor-trust">
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: ShieldCheck,    label: 'Licensed, verified providers' },
              { icon: GraduationCap,  label: 'Experience-led comparison' },
              { icon: ClipboardCheck, label: 'Practical, written criteria' },
              { icon: Heart,          label: 'Independent — not a hospital' },
            ].map(({ icon: Icon, label }, i) => (
              <div key={label} className="flex items-center gap-3" data-testid={`choose-doctor-trust-${i + 1}`}>
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: '#DCEFEC' }}
                >
                  <Icon className="h-4 w-4 text-[#1A8F8A]" />
                </div>
                <p className="text-xs font-bold text-slate-800 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTRODUCTION ─────────────────────────────────────────────────── */}
      <UrgentIntro
        heading="Choosing a doctor is one of the most important decisions you make."
        paragraphs={[
          'Whether you are planning surgery, dental treatment, fertility care, a health checkup or anything in between, the doctor you choose shapes your experience and your outcome. This guide is independent — we are not a clinic or hospital, and we do not promote any single doctor. Our goal is simple: help you compare options sensibly and ask the questions that matter.',
          'You will find a clear comparison framework, a practical checklist, the questions to ask before deciding, the red flags to watch for, and notes on when to seek a second opinion. Everything on this page is based on the same internal vetting checklist VietClinics uses with partner doctors and clinics.',
          'We never claim "the best doctor in Vietnam" — that is a guidance question, not a fact. Different patients need different doctors. The right doctor for you is the one whose experience, facility, communication style and aftercare match your specific case.',
        ]}
        testId="choose-doctor-intro"
      />

      {/* ── WHY DOCTOR CHOICE MATTERS ────────────────────────────────────── */}
      <UrgentCardGrid
        eyebrow="Why doctor choice matters"
        heading="Four reasons price alone is the wrong way to choose"
        items={WHY_CHOICE_MATTERS}
        testId="choose-doctor-why"
      />

      {/* ── 10 COMPARISON FACTORS ────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="choose-doctor-factors">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            The 10-factor comparison framework
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            What to compare across doctors
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {COMPARISON_FACTORS.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`choose-doctor-factor-${i + 1}`}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white font-extrabold text-xs flex-shrink-0 mt-0.5"
                  style={{ background: '#1FA7A1' }}
                >
                  {i + 1}
                </span>
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── CHECKLIST BEFORE DECIDING ────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="choose-doctor-checklist">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Practical checklist before deciding
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Tick these off before you commit
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {CHECKLIST_BEFORE_DECIDING.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-2xl p-4"
                style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
                data-testid={`choose-doctor-checklist-${i + 1}`}
              >
                <CheckCircle2 className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── RED FLAGS ────────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="choose-doctor-redflags">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-amber-700 uppercase tracking-widest mb-3">
            Red flags to watch for
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            Six signals that should make you pause
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RED_FLAGS.map((rf, i) => (
              <div
                key={i}
                className="bg-amber-50/40 border border-amber-100 rounded-2xl p-5"
                data-testid={`choose-doctor-redflag-${i + 1}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="font-extrabold text-amber-900 text-sm leading-snug">{rf.title}</p>
                </div>
                <p className="text-sm text-amber-900/80 leading-relaxed">{rf.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── QUESTIONS TO ASK ─────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#F7FAF9]" data-testid="choose-doctor-questions">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Questions to ask before choosing
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            The 10 questions every patient should ask
          </h2>
          <div className="space-y-3">
            {QUESTIONS_TO_ASK.map((q, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl px-5 py-4"
                style={{ boxShadow: '0 2px 12px rgba(15,42,85,0.04)' }}
                data-testid={`choose-doctor-question-${i + 1}`}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white font-extrabold text-xs flex-shrink-0 mt-0.5"
                  style={{ background: '#1FA7A1' }}
                >
                  {i + 1}
                </span>
                <p className="text-sm md:text-base text-slate-700 font-semibold leading-snug">{q}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECOND OPINION ───────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="choose-doctor-second-opinion">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            When to seek a second opinion
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-6">
            A second opinion is normal — not a sign of distrust
          </h2>
          <p className="text-base lg:text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl">
            For surgery, complex dental work, fertility care, oncology and other consequential
            decisions, a second opinion can confirm the plan, surface alternative approaches,
            or simply give you confidence. Reasonable doctors expect it.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3">
            {SECOND_OPINION_REASONS.map((r, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-[#F0FAF8] border border-[#DCEFEC] rounded-2xl p-4"
                data-testid={`choose-doctor-second-opinion-${i + 1}`}
              >
                <CheckCircle2 className="h-5 w-5 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700 font-semibold leading-snug">{r}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── HOW VIETCLINICS HELPS ────────────────────────────────────────── */}
      <UrgentSteps
        eyebrow="How VietClinics helps you choose"
        heading="From enquiry to confident decision"
        steps={SUPPORT_JOURNEY}
        testId="choose-doctor-journey"
      />

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <UrgentFaq
        heading="How to choose a doctor in Vietnam — frequently asked"
        items={FAQ}
        testId="choose-doctor-faq"
      />

      {/* ── RELATED LINKS ────────────────────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-white" data-testid="choose-doctor-related">
        <div className="container mx-auto px-4 max-w-6xl">
          <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
            Continue exploring VietClinics
          </p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-[1.15] mb-8">
            More patient support &amp; treatment guides
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RELATED_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl p-5 transition-colors flex items-start justify-between gap-4"
                data-testid={`choose-doctor-related-${link.to.replace(/\W+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">{link.label}</p>
                  <p className="text-xs text-slate-500 mt-1 leading-snug">{link.sub}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── EDITORIAL ────────────────────────────────────────────────────── */}
      <UrgentEditorial editorial={EDITORIAL} testId="choose-doctor-editorial" />

      {/* ── DISCLAIMER ───────────────────────────────────────────────────── */}
      <section className="py-10 bg-white" data-testid="choose-doctor-disclaimer-section">
        <div className="container mx-auto px-4 max-w-4xl">
          <div
            className="bg-amber-50/60 border border-amber-100 rounded-2xl p-5 lg:p-6 flex gap-3"
            data-testid="choose-doctor-disclaimer"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm leading-relaxed text-amber-900">
              <p className="font-bold mb-1">Important notice</p>
              <p>
                VietClinics is an independent healthcare coordination platform.
                VietClinics is <strong>not a hospital or clinic</strong> and does
                <strong> not provide medical diagnosis</strong>. This guide is
                educational. All medical advice, diagnosis and treatment decisions
                must come from licensed healthcare professionals. For life-threatening
                emergencies in Vietnam, dial <strong>115</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <UrgentCtaBand
        heading="Want Help Choosing the Right Doctor in Vietnam?"
        subheading="Tell VietClinics about your case and we will share suitable doctor and clinic options, help you compare them, and coordinate consultations — without pressure."
        testId="choose-doctor-final-cta"
      />

      <Footer />
    </div>
  );
};

export default HowToChooseDoctorVietnamPage;
